import { ArrowBackOutlined, RefreshOutlined } from '@mui/icons-material';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PageAppBar } from 'src/components/pageAppBar';
import { UserContext } from 'src/contexts/UserContext';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle } from 'src/theme';
import {
	getContactRecord,
	getStageContacts,
	getStageCounts,
	TRACKING_STAGE_LABELS,
	TRACKING_STAGES,
} from 'src/tools/contactFlowTrackingService';

/**
 * Contact Flow Tracking.
 *
 * Landing view lists every active import for the tenant with per-stage counts
 * pulled from `/cache/tracking/stage-counts`. Clicking any stage count drills
 * into `/cache/tracking/contacts` to show the individual contacts currently
 * sitting in that stage of that import.
 *
 * @category Pages
 * @component
 */
export function ContactFlowTracking() {
	const userContext = useContext(UserContext);

	// null = showing the imports landing; otherwise the drilled-into stage.
	const [drilldown, setDrilldown] = useState(null);

	return (
		<Box>
			{drilldown ? (
				<StageContacts
					apiKey={userContext.apiKey}
					importId={drilldown.importId}
					stage={drilldown.stage}
					onBack={() => setDrilldown(null)}
				/>
			) : (
				<ImportsOverview
					apiKey={userContext.apiKey}
					ready={!!userContext.tenantId}
					onDrill={(importId, stage) => setDrilldown({ importId, stage })}
				/>
			)}
		</Box>
	);
}

/**
 * Landing table: one row per import, a total column, and a clickable count for
 * each stage. Includes a client-side name filter.
 */
function ImportsOverview({ apiKey, ready, onDrill }) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [imports, setImports] = useState([]);
	const [totals, setTotals] = useState({ ingested: 0, indexed: 0, queued: 0, completed: 0 });
	const [filter, setFilter] = useState('');

	const load = useCallback(async () => {
		if (!apiKey) return;
		setLoading(true);
		setError('');
		try {
			const data = await getStageCounts(apiKey);
			setImports(data.imports);
			setTotals(data.totals);
		} catch (err) {
			console.error('Failed to load stage counts', err);
			setError('Unable to load import tracking data.');
		} finally {
			setLoading(false);
		}
	}, [apiKey]);

	useEffect(() => {
		if (ready) {
			load();
		}
	}, [ready, load]);

	const filtered = useMemo(() => {
		const needle = filter.trim().toLowerCase();
		if (!needle) return imports;
		return imports.filter((imp) => imp.importId.toLowerCase().includes(needle));
	}, [imports, filter]);

	return (
		<>
			<PageAppBar
				title="Contact Flow Tracking"
				description="Beta — this data is a best-effort view of contact import status. It is not historical, and completed-state data expires within the hour."
			/>
			<Card variant="outlined" sx={cardStyle}>
				<CardContent>
					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						spacing={2}
						alignItems={{ xs: 'stretch', sm: 'center' }}
						justifyContent="space-between"
						sx={{ mb: 2 }}
					>
						<TextField
							label="Filter imports"
							placeholder="Search by import name"
							variant="outlined"
							size="small"
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
							sx={{ minWidth: 260 }}
						/>
						<Button
							startIcon={<RefreshOutlined />}
							onClick={load}
							sx={actionTwoButtonStyle}
							disabled={loading}
						>
							Refresh
						</Button>
					</Stack>

					{error && (
						<Typography color="error" sx={{ mb: 2 }}>
							{error}
						</Typography>
					)}

					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>Import</TableCell>
									{TRACKING_STAGES.map((stage) => (
										<TableCell key={stage} align="center" sx={{ fontWeight: 700 }}>
											{TRACKING_STAGE_LABELS[stage]}
										</TableCell>
									))}
									<TableCell align="center" sx={{ fontWeight: 700 }}>
										Total
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading && (
									<TableRow>
										<TableCell colSpan={TRACKING_STAGES.length + 2} align="center" sx={{ py: 4 }}>
											<CircularProgress size={28} />
										</TableCell>
									</TableRow>
								)}

								{!loading && filtered.length === 0 && (
									<TableRow>
										<TableCell colSpan={TRACKING_STAGES.length + 2} align="center" sx={{ py: 4 }}>
											<Typography color="text.secondary">
												{imports.length === 0
													? 'No active imports are being tracked.'
													: 'No imports match your filter.'}
											</Typography>
										</TableCell>
									</TableRow>
								)}

								{!loading &&
									filtered.map((imp) => (
										<TableRow key={imp.importId} hover>
											<TableCell sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
												{imp.importId}
											</TableCell>
											{TRACKING_STAGES.map((stage) => {
												const count = imp.counts?.[stage] ?? 0;
												return (
													<TableCell key={stage} align="center">
														<Tooltip
															title={
																count > 0
																	? `View ${TRACKING_STAGE_LABELS[stage]} contacts`
																	: `No contacts in ${TRACKING_STAGE_LABELS[stage]}`
															}
														>
															<span>
																<Button
																	size="small"
																	disabled={count === 0}
																	onClick={() => onDrill(imp.importId, stage)}
																>
																	{count.toLocaleString()}
																</Button>
															</span>
														</Tooltip>
													</TableCell>
												);
											})}
											<TableCell align="center">
												<Chip label={(imp.total ?? 0).toLocaleString()} size="small" />
											</TableCell>
										</TableRow>
									))}
							</TableBody>
							{!loading && filtered.length > 0 && (
								<TableFooter>
									<TableRow>
										<TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
											Totals (all imports)
										</TableCell>
										{TRACKING_STAGES.map((stage) => (
											<TableCell key={stage} align="center" sx={{ fontWeight: 700, color: 'text.primary' }}>
												{(totals?.[stage] ?? 0).toLocaleString()}
											</TableCell>
										))}
										<TableCell align="center" sx={{ fontWeight: 700, color: 'text.primary' }}>
											{Object.values(totals || {})
												.reduce((sum, n) => sum + (Number(n) || 0), 0)
												.toLocaleString()}
										</TableCell>
									</TableRow>
								</TableFooter>
							)}
						</Table>
					</TableContainer>
				</CardContent>
			</Card>
		</>
	);
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Drill-down table: paginated contacts for a single import + stage, with a
 * page-scoped search (matching the backend's page-scoped filter).
 */
function StageContacts({ apiKey, importId, stage, onBack }) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [contacts, setContacts] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(100);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [selectedContactId, setSelectedContactId] = useState(null);

	const load = useCallback(async () => {
		if (!apiKey) return;
		setLoading(true);
		setError('');
		try {
			const data = await getStageContacts({
				apiKey,
				importId,
				stage,
				limit: rowsPerPage,
				offset: page * rowsPerPage,
				search: search || undefined,
			});
			setContacts(data.contacts);
			setTotal(data.total);
		} catch (err) {
			console.error('Failed to load stage contacts', err);
			setError('Unable to load contacts for this stage.');
		} finally {
			setLoading(false);
		}
	}, [apiKey, importId, stage, page, rowsPerPage, search]);

	useEffect(() => {
		load();
	}, [load]);

	function applySearch() {
		setPage(0);
		setSearch(searchInput.trim());
	}

	function clearSearch() {
		setSearchInput('');
		setPage(0);
		setSearch('');
	}

	return (
		<>
			<PageAppBar title={`${TRACKING_STAGE_LABELS[stage]} Contacts`} />
			<Card variant="outlined" sx={cardStyle}>
				<CardContent>
					<Button startIcon={<ArrowBackOutlined />} onClick={onBack} sx={{ mb: 2 }}>
						Back to imports
					</Button>

					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
						<Chip label={`Import: ${importId}`} sx={{ maxWidth: 420 }} />
						<Chip color="primary" label={`Stage: ${TRACKING_STAGE_LABELS[stage]}`} />
						<Chip variant="outlined" label={`${total.toLocaleString()} total in stage`} />
					</Stack>

					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						spacing={2}
						alignItems={{ xs: 'stretch', sm: 'center' }}
						sx={{ mb: 2 }}
					>
						<TextField
							label="Search this page"
							placeholder="Name, external ID, or phone"
							variant="outlined"
							size="small"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') applySearch();
							}}
							helperText="Filters the current page only"
							sx={{ minWidth: 280 }}
							InputProps={{
								endAdornment: search ? (
									<InputAdornment position="end">
										<Button size="small" onClick={clearSearch}>
											Clear
										</Button>
									</InputAdornment>
								) : null,
							}}
						/>
						<Button onClick={applySearch} sx={actionOneButtonStyle} disabled={loading}>
							Search
						</Button>
						<Box sx={{ flexGrow: 1 }} />
						<Button
							startIcon={<RefreshOutlined />}
							onClick={load}
							sx={actionTwoButtonStyle}
							disabled={loading}
						>
							Refresh
						</Button>
					</Stack>

					{error && (
						<Typography color="error" sx={{ mb: 2 }}>
							{error}
						</Typography>
					)}

					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>External ID</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Entered Stage</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading && (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 4 }}>
											<CircularProgress size={28} />
										</TableCell>
									</TableRow>
								)}

								{!loading && contacts.length === 0 && (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 4 }}>
											<Typography color="text.secondary">
												{search
													? 'No contacts on this page match your search.'
													: 'No contacts in this stage.'}
											</Typography>
										</TableCell>
									</TableRow>
								)}

								{!loading &&
									contacts.map((contact) => (
										<TableRow
											key={contact.id}
											hover
											onClick={() => setSelectedContactId(contact.id)}
											sx={{ cursor: 'pointer' }}
										>
											<TableCell>{contact.name ?? '—'}</TableCell>
											<TableCell>{contact.externalId ?? '—'}</TableCell>
											<TableCell>{contact.phone ?? '—'}</TableCell>
											<TableCell>
												{contact.enteredAt
													? new Date(contact.enteredAt).toLocaleString()
													: '—'}
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component="div"
						count={total}
						page={page}
						onPageChange={(e, newPage) => setPage(newPage)}
						rowsPerPage={rowsPerPage}
						rowsPerPageOptions={PAGE_SIZE_OPTIONS}
						onRowsPerPageChange={(e) => {
							setRowsPerPage(parseInt(e.target.value, 10));
							setPage(0);
						}}
					/>
				</CardContent>
			</Card>

			<ContactDetailDialog
				open={!!selectedContactId}
				apiKey={apiKey}
				importId={importId}
				contactId={selectedContactId}
				onClose={() => setSelectedContactId(null)}
			/>
		</>
	);
}

/** Format an epoch-ms timestamp for display, tolerating null/undefined. */
function formatTimestamp(ms) {
	return ms ? new Date(ms).toLocaleString() : '—';
}

/** A single label/value row in the detail dialog. */
function DetailField({ label, value }) {
	return (
		<Grid item xs={12} sm={6}>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
				{value ?? '—'}
			</Typography>
		</Grid>
	);
}

/**
 * Popup showing the full tracking record for one contact, fetched on open from
 * `/cache/tracking/contact`.
 */
function ContactDetailDialog({ open, apiKey, importId, contactId, onClose }) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [record, setRecord] = useState(null);

	useEffect(() => {
		if (!open || !apiKey || !importId || !contactId) return;

		let cancelled = false;
		async function load() {
			setLoading(true);
			setError('');
			setRecord(null);
			try {
				const data = await getContactRecord({ apiKey, importId, contactId });
				if (!cancelled) setRecord(data);
			} catch (err) {
				console.error('Failed to load contact record', err);
				if (!cancelled) {
					setError(
						err?.response?.statusCode === 404
							? 'This contact record has expired or is no longer available.'
							: 'Unable to load contact details.'
					);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [open, apiKey, importId, contactId]);

	const stageTimes = record?.stageTimes ?? {};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Contact Details</DialogTitle>
			<DialogContent dividers>
				{loading && (
					<Box display="flex" justifyContent="center" sx={{ py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				)}

				{!loading && error && <Typography color="error">{error}</Typography>}

				{!loading && !error && record && (
					<Stack spacing={2}>
						<Grid container spacing={2}>
							<DetailField label="Name" value={record.name} />
							<DetailField label="External ID" value={record.externalId} />
							<DetailField label="Phone" value={record.phone} />
							<DetailField label="Contact ID" value={record.contactId} />
							<DetailField label="Import" value={record.importId} />
							<DetailField label="Import Source" value={record.importSource} />
							<DetailField
								label="Current Stage"
								value={TRACKING_STAGE_LABELS[record.stage] ?? record.stage}
							/>
							<DetailField
								label="Outcome"
								value={record.outcome ?? (record.terminal ? '—' : 'In progress')}
							/>
							<DetailField label="First Seen" value={formatTimestamp(record.firstSeenAt)} />
							<DetailField label="Last Updated" value={formatTimestamp(record.updatedAt)} />
						</Grid>

						<Divider />

						<Box>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Stage Timeline
							</Typography>
							<Grid container spacing={2}>
								<DetailField label="Ingested" value={formatTimestamp(stageTimes.INGESTED)} />
								<DetailField label="Indexed" value={formatTimestamp(stageTimes.INDEXED)} />
								<DetailField label="Queued" value={formatTimestamp(stageTimes.QUEUED)} />
								<DetailField label="Completed" value={formatTimestamp(stageTimes.completed)} />
							</Grid>
						</Box>
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}
